import { useCallback, useMemo } from 'react';

import { useNavigate } from 'react-router-dom';

import { getEnvVariable } from '@appcharge/shared-ui';
import { QueryCache, useQueryClient } from '@tanstack/react-query';
import { EApiRoutes } from 'api/api.utils';
import usePermissions from 'api/usePermissions';
import { removeUserAuthData } from 'utils/login';

import { EUserTypeId, Portal, Project, UserRole } from '../common/contracts';
import { TIME_TO_STORE_JWT_TOKEN } from '../constants/constants';
import { ELocalStorageKeys, ELogoutMessage } from '../constants/enums';
import { UserDetails } from '../pages/Login/Login.types';
import { localStorageUtil } from '../utils/localStorageUtil';

const queryCache = new QueryCache({
  onError: (error) => {
    console.error(error);
  }
});

export const useLogin = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setPermissions } = usePermissions();

  const cleanUserLoginData = (message?: ELogoutMessage) => {
    removeUserAuthData();
    queryCache.clear();
    queryClient.clear();
    navigate('/', { state: { logoutMessage: message } });
  };

  const saveLoginDetails = (detailsData: {
    email: string;
    firstName: string;
    lastName: string;
    userId: string;
    isSession: boolean;
    token?: string;
    role?: UserRole;
    typeId?: string;
    projects?: Project[];
    portals?: Portal[];
    version?: number;
    company?: string;
    isOnboarding?: boolean;
    companyDisplayName?: string;
  }) => {
    queryClient.clear();
    if (detailsData.token) {
      localStorageUtil.remove(
        ELocalStorageKeys.JWT_TOKEN,
        detailsData.isSession
      );
      localStorageUtil.remove(
        ELocalStorageKeys.JWT_TOKEN_TTL,
        detailsData.isSession
      );
      localStorageUtil.set(
        ELocalStorageKeys.JWT_TOKEN,
        detailsData.token,
        detailsData.isSession
      );
      localStorageUtil.set(
        ELocalStorageKeys.JWT_TOKEN_TTL,
        new Date(Date.now() + TIME_TO_STORE_JWT_TOKEN).getTime(),
        detailsData.isSession
      );
    }
    const userDetails: UserDetails = {
      email: detailsData.email,
      firstName: detailsData.firstName,
      lastName: detailsData.lastName,
      userId: detailsData.userId,
      userName: `${detailsData.firstName} ${detailsData.lastName}`,
      isOnboarding: detailsData.isOnboarding
    };
    if (detailsData.typeId && detailsData.role) {
      userDetails.role =
        detailsData.typeId === EUserTypeId.APPCHARGE
          ? UserRole.SUPER_ADMIN
          : detailsData.role;
    }
    if (detailsData.projects) {
      userDetails.projects = detailsData.projects;
    }
    if (detailsData.version) {
      userDetails.version = detailsData.version;
      userDetails.role = UserRole.SUPER_ADMIN;
    }

    if (detailsData?.company) {
      userDetails.company = detailsData.company;
    }

    if (detailsData?.companyDisplayName) {
      userDetails.companyDisplayName = detailsData.companyDisplayName;
    }

    if (detailsData?.portals) {
      userDetails.portals = detailsData.portals;
    }

    // For MFA (no token), use localStorage so auth persists across tabs
    if (!detailsData.token && detailsData.userId) {
      localStorageUtil.set(
        ELocalStorageKeys.JWT_TOKEN,
        `cookie-auth:${detailsData.userId}`,
        false
      );
      localStorageUtil.set(
        ELocalStorageKeys.JWT_TOKEN_TTL,
        new Date(Date.now() + TIME_TO_STORE_JWT_TOKEN).getTime(),
        false
      );
      localStorageUtil.set(
        ELocalStorageKeys.USER_DETAILS,
        userDetails,
        false
      );
    }

    const hasCompleteData = userDetails.projects !== undefined && userDetails.portals !== undefined;
    if (hasCompleteData) {
      queryClient.setQueryData([EApiRoutes.USERS], userDetails);
    } else {
      queryClient.invalidateQueries({ queryKey: [EApiRoutes.USERS] });
    }
    
    if (detailsData.version) {
      setPermissions();
    }
  };

  const isLoggedIn = useCallback(() => {
    const isUserAuthenticatedLS = localStorageUtil.getAny(
      ELocalStorageKeys.JWT_TOKEN
    );
    const userAuthTTL = localStorageUtil.getAny(
      ELocalStorageKeys.JWT_TOKEN_TTL
    );
    if (isUserAuthenticatedLS && userAuthTTL) {
      const isTokenValid = isUserAuthenticatedLS && userAuthTTL - new Date().getTime() > 0;
      if (isTokenValid) {
        return true;
      }
    }
    
    const cachedUserDetails = queryClient.getQueryData<UserDetails>([EApiRoutes.USERS]);
    if (cachedUserDetails && cachedUserDetails.userId) {
      return true;
    }
    
    return false;
  }, []);

  const cachedUserDetails = queryClient.getQueryData<UserDetails>([EApiRoutes.USERS]);
  
  const userDetails = cachedUserDetails ?? null;
  
  const calculateMustInitiateProject = useCallback((userDetailsToCheck: UserDetails | null, isLoading?: boolean) => {
    const loggedIn = isLoggedIn();
    
    if (!userDetailsToCheck || isLoading) {
      return false;
    }

    const { projects, portals } = userDetailsToCheck;
    if (!projects || !portals) {
      return false;
    }

    const hasProjects = projects.length > 0;
    const hasPortals = portals.length > 0;

    return (
      getEnvVariable('REACT_APP_ENV') !== 'production' &&
      loggedIn &&
      !(hasProjects || hasPortals)
    );
  }, [isLoggedIn]);
  
  const mustInitiateProject = useMemo(() => {
    return calculateMustInitiateProject(userDetails);
  }, [userDetails, calculateMustInitiateProject]);

  return {
    saveLoginDetails,
    cleanUserLoginData,
    isLoggedIn,
    mustInitiateProject,
    calculateMustInitiateProject
  };
};
